import { createEffect, createEvent, createStore, sample } from "effector";
import { onboardingMeta } from "@data/onboardings/meta";
import { IStudent } from "../../../shared/entities/student";
import { reset } from "patronum";
import { $activeRoute, appMounted, routePathNameChanged } from "../../../app/model";
import { ModelContract, ModuleDescription } from "../../../shared/module-description";
import { routerPush, routerReplace } from "@features/shared/router";
import { HttpRequest } from "@features/shared/request/model";
import { toastShow } from "@features/notification/model";
import { ICustomError } from "@features/shared/entities/notification";
import {
    Credentials,
    IAppleRestorePurchase,
    PaymentApi,
    SignupCredentials,
    StudentApi,
    CourseDataApi
} from "@features/shared/api/course-data.api";
import { DeviceDataPayload } from "@features/shared/entities/features-api";
import { LoginScreenCompleted, userEmailCollected, getDeviceId } from "@features/shared/events";
import { persistMetaPredictionEmail } from "@features/shared/meta-events";
import { emailInputValueChanged, inputValueChanged, nextStep } from "@features/onboarding/ui/current";
import { AnalyticsService } from "@features/shared/analytics";
import { $isApp, $platform } from "@features/launch-context/model";
import { changeEmailValue } from "@features/landing/ui/luck-landing";
import { logoutOneSignal, setOneSignalExternalId } from "@features/shared/onesignal";
import { notRequireAuthPaths } from "@features/notRequiredAuthPaths";
import { changeEmailValueOnCreator } from "@features/landing/ui/creator-landing";
import { identify } from "@features/flagsmith/model";

const signInRequested = createEvent<Credentials>();
const signUpRequested = createEvent<SignupCredentials>();
const signinError = createEvent<string>();
export const deleteProfileRequested = createEvent<void>();
const setSignupStatus = createEvent<boolean>();
export const getAppleRestorePurchases = createEvent<IAppleRestorePurchase>();
const magicLinkTokenDetected = createEvent<string>();
const magicLinkAuthenticated = createEvent();

const authenticated = createEvent();
export const logoutStudent = createEvent();
const studentProfileRefreshed = createEvent();

const $student = createStore<IStudent | null>(null, { skipVoid: false });
const setStudentActiveLocally = createEvent();

$student.on(setStudentActiveLocally, (student) => {
    if (!student) return student;
    return { ...student, status: "active" };
});

const $signinError = createStore<string>("")
    .on(signinError, (_, p) => p)
    .reset(inputValueChanged, emailInputValueChanged, changeEmailValue, changeEmailValueOnCreator);

const $signupSuccess = createStore(false)
    .on(setSignupStatus, (_, s) => s)
    .reset(routePathNameChanged);

const $appleRestoreResult = createStore<string | null>(null).reset(routePathNameChanged);
const $appleRestoreError = createStore<string | null>(null).reset(routePathNameChanged);

const authenticateFx = createEffect(async () => {
    const token = await HttpRequest.tokenStorage.getToken();
    if (!token) throw new Error("No token");
    return await StudentApi.readProfile();
});


// Prevent early redirects caused by $activeRoute.updates before the initial auth check finishes
const $authChecked = createStore(false).on(authenticateFx.finally, () => true);

const trackUserEmailCollectedFx = createEffect(() => {
    const email = sessionStorage.getItem("email") ?? "";
    const onboardingId = sessionStorage.getItem("onboarding_id") ?? undefined;
    userEmailCollected({ onboarding: onboardingId, email });
});

const trackLoginFx = createEffect(() => {
    LoginScreenCompleted();
});

const deleteProfileFx = createEffect(async () => {
    await StudentApi.deleteProfile();
});

const clearUserDataFromStorageFx = createEffect(async () => {
    if (typeof window !== "undefined") {
        localStorage.removeItem("user_public_uuid");
        localStorage.removeItem("user_uuid");
        sessionStorage.removeItem("email");
        sessionStorage.removeItem("onboarding_answers");
        sessionStorage.removeItem("onboarding_id");
    }
});

const restoreApplePurchaseFx = createEffect(async (data: IAppleRestorePurchase) => {
    const { transactionId, appAccountToken } = data;
    if (!transactionId || !appAccountToken) return null;

    const response = await PaymentApi.appleRestorePurchase(transactionId, appAccountToken);
    return response;
});

const signInByEmailFx = createEffect(async (data: { email: string; password: string }) => {
    const response = await StudentApi.signInByEmail(data);
    HttpRequest.tokenStorage.setToken(response);
    return response;
});

const verifyMagicLinkFx = createEffect(async (token: string) => {
    const existingToken = await HttpRequest.tokenStorage.getToken();
    if (existingToken) return null;

    const jwt = await StudentApi.verifyMagicLink(token);
    await HttpRequest.tokenStorage.setToken(jwt);
    return token;
});

interface SignUpResponse {
    userPublicId: string;
    userId: number;
}

const signUpByEmailFx = createEffect<SignupCredentials, SignUpResponse, Error>(async (data) => {
    const response = (await StudentApi.singUpByEmail(data)) as SignUpResponse;

    localStorage.setItem("user_public_uuid", response.userPublicId);
    return response;
});

const clearEmailFromStorageFx = createEffect(async () => {
    sessionStorage.removeItem("email");
});

const trackEvent = createEffect((eventName: string) => AnalyticsService.trackEvent(eventName));

// Identifies the analytics person (PostHog identify + student_id person prop).
// Name kept from the Amplitude era: the flags model and the upsell modules are
// wired to this effect.
const setUserIdInAmplitudeFx = createEffect(async (userId: number) => {
    if (typeof window === "undefined") return;
    AnalyticsService.setUserId(userId);
    console.log("[analytics] set userId with stable deviceId:", getDeviceId());
});

// First-login / magic-link sessions: the backend events-table device id adopts
// the purchase funnel's provider id (FunnelFox `pro_*` → `fnlfx_*`) so the
// app rows join the funnel rows. PostHog identity is untouched — the person is
// already student_N by this point.
const setAmplitudeDeviceIdFromProviderFx = createEffect((providerId: string) => {
    if (typeof window === "undefined") return;
    if (!providerId) return;
    const deviceId = providerId.startsWith("pro_") ? `fnlfx_${providerId.slice("pro_".length)}` : providerId;
    localStorage.setItem("user_uuid", deviceId);
    console.log("[analytics] overrode backend deviceId with provider_id:", deviceId);
});

const trackDeviceDataFx = createEffect(async () => {
    if (typeof window === "undefined") return;

    const deviceId = localStorage.getItem("user_uuid");
    if (!deviceId) return;

    const payload: DeviceDataPayload = {
        device_id: deviceId,
        visit_url: window.location.href
    };

    await CourseDataApi.trackDeviceData(payload);
});

export const getOnboardingDataFx = createEffect(async () => {
    return await StudentApi.getOnboardingData();
});

const saveOnboardingAnswersFx = createEffect(
    async ({ answers, email, onboardingName }: { answers: any; email: string; onboardingName: string }) => {
        return await StudentApi.saveOnboardingAnswers({ answers, email, onboardingName });
    }
);

const confirmEmailAfterPurchaseFx = createEffect(
    async ({ emailFromPurchase, newEmail }: { emailFromPurchase: string; newEmail: string }) => {
        try {
            AnalyticsService.setUserProps({ confirmed_email: newEmail });
            if (emailFromPurchase === newEmail) {
                const response = await StudentApi.confirmEmailAfterPurchase(emailFromPurchase);
                return response;
            } else {
                const response = await StudentApi.confirmEmailAfterPurchase(emailFromPurchase, newEmail);
                return response;
            }
        } catch (error) {
            throw error;
        }
    }
);

const setPasswordWithoutCodeFx = createEffect(async ({ email, password }: { email: string; password: string }) => {
    try {
        const response = await StudentApi.setPasswordWithoutCode(email, password);
        return response;
    } catch (error) {
        throw error;
    }
});

const refreshStudentProfileFx = createEffect(async () => {
    return await StudentApi.readProfile();
});

sample({
    clock: signUpByEmailFx.done,
    filter: () => {
        if (typeof window === "undefined") return false;
        return !!sessionStorage.getItem("onboarding_answers") && !!sessionStorage.getItem("email");
    },
    fn: () => {
        const answers = JSON.parse(sessionStorage.getItem("onboarding_answers") || "[]");
        const email = sessionStorage.getItem("email") || "";
        let onboardingName = sessionStorage.getItem("onboarding_id") || "";

        if (!onboardingName) {
            const pathname = window.location.pathname;
            const pathSegments = pathname.split("/").filter(Boolean);
            const onboardingSlug = pathSegments[1];

            if (pathSegments[0] === "onboarding" && onboardingSlug) {
                const matchedBySlug = onboardingMeta.find((onboarding) => onboarding.pathname === onboardingSlug);
                if (matchedBySlug) {
                    onboardingName = matchedBySlug.id;
                }
            }

            if (!onboardingName) {
                const matched = onboardingMeta.find((onboarding) => pathname.startsWith(onboarding.pathname));
                if (matched) {
                    onboardingName = matched.id;
                }
            }
        }

        return { answers, email, onboardingName };
    },
    target: saveOnboardingAnswersFx
});

sample({
    clock: signInByEmailFx.fail,
    fn: () => "Incorrect password",
    target: signinError
});

sample({
    clock: deleteProfileRequested,
    target: [deleteProfileFx, logoutStudent]
});

sample({
    clock: deleteProfileFx.done,
    fn: () => "user_deleted",
    target: trackEvent
});

sample({
    clock: deleteProfileFx.done,
    target: clearUserDataFromStorageFx
});

sample({
    clock: signUpByEmailFx.fail,
    fn: () => "Email already exists",
    target: signinError
});

sample({
    clock: signUpByEmailFx.done,
    filter: () => {
        if (typeof window === "undefined") return true;
        const onboardingId = sessionStorage.getItem("onboarding_id");
        return onboardingId !== "email_onboarding";
    },
    target: nextStep
});

sample({
    clock: signUpByEmailFx.done,
    filter: () => {
        if (typeof window === "undefined") return true;
        const onboardingId = sessionStorage.getItem("onboarding_id");
        return onboardingId !== "email_onboarding";
    },
    target: trackUserEmailCollectedFx
});

sample({
    clock: signUpByEmailFx.done,
    fn: () => true,
    target: setSignupStatus
});

sample({
    clock: signUpByEmailFx.doneData,
    filter: (response) => response?.userId !== undefined,
    fn: (response) => response.userId,
    target: setUserIdInAmplitudeFx
});

sample({
    clock: signUpByEmailFx.done,
    target: trackDeviceDataFx
});

sample({
    clock: authenticateFx.done,
    target: trackDeviceDataFx
});

sample({
    clock: signUpByEmailFx.fail,
    fn: () => false,
    target: setSignupStatus
});

sample({
    clock: signUpByEmailFx.fail,
    target: clearEmailFromStorageFx
});

sample({
    clock: $signupSuccess,
    filter: () =>
        typeof window !== "undefined" &&
        (window.location.pathname === "/luck/set-email" || window.location.pathname === "/creator/set-email"),
    fn: () => "paywall",
    target: routerPush
});

sample({
    clock: [appMounted, signInByEmailFx.done],
    target: authenticateFx
});

sample({
    clock: signInByEmailFx.done,
    target: trackLoginFx
});

sample({
    clock: signInByEmailFx.fail,
    filter: ({ error }: { error: ICustomError }) => error?.error?.title !== null,
    fn: ({ error }: { error: ICustomError }) => error?.error,
    target: toastShow
});

export const logoutFx = createEffect(async () => {
    await HttpRequest.tokenStorage.removeToken();
    logoutOneSignal();
});

// Detaches the analytics person (PostHog + backend device id) so post-logout activity
// starts a fresh anonymous identity instead of landing on the old student.
// Deliberately NOT wired to logoutStudent: that also fires on
// authenticateFx.fail, and a transient auth failure must not shred identity.
export const analyticsResetFx = createEffect(() => AnalyticsService.reset());

sample({
    clock: deleteProfileRequested,
    target: analyticsResetFx
});

reset({
    clock: logoutStudent,
    target: $student
});

sample({
    clock: logoutStudent,
    target: logoutFx
});

sample({
    clock: authenticateFx.fail,
    target: logoutStudent
});

sample({
    clock: authenticateFx.doneData,
    target: $student
});

// The Meta prediction request (features/shared/meta-events.ts) runs outside React and
// cannot read the scoped $student; mirror the signed-in email for it (cleared on logout).
const persistMetaPredictionEmailFx = createEffect((email: string | null) => persistMetaPredictionEmail(email));

sample({
    clock: $student.updates,
    fn: (student) => student?.email ?? null,
    target: persistMetaPredictionEmailFx
});

sample({
    clock: authenticateFx.doneData,
    target: getOnboardingDataFx
});

sample({
    clock: authenticateFx.doneData,
    filter: (student) => !!student && !!student.id,
    fn: (student) => student.id,
    target: setUserIdInAmplitudeFx
});

const identifyUserFx = createEffect((id: string) => {
    if (typeof window !== "undefined") {
        identify(id);
    }
});

const setOneSignalUserFx = createEffect((userId: string) => {
    setOneSignalExternalId(userId);
});

sample({
    clock: authenticateFx.doneData,
    filter: (student) => !!student && !!student.id,
    fn: (student) => String(student.id),
    target: identifyUserFx
});

sample({
    clock: authenticateFx.doneData,
    filter: (student) => !!student && !!student.id,
    fn: (student) => String(student.id),
    target: setOneSignalUserFx
});

sample({
    clock: authenticateFx.done,
    target: authenticated
});

sample({
    clock: signInRequested,
    fn: (data) => {
        data.email = data.email.trim().toLowerCase();

        return data;
    },
    target: signInByEmailFx
});

sample({
    clock: signUpRequested,
    fn: (data) => {
        data.email = data.email.trim().toLowerCase();

        return data;
    },
    target: signUpByEmailFx
});

// The `callbackUrl` query param of /auth/signin, if it points inside the app.
const getInternalCallbackUrl = (): string | null => {
    if (typeof window === "undefined") return null;

    const callbackUrl = new URLSearchParams(window.location.search).get("callbackUrl");
    const isInternalPath =
        !!callbackUrl && callbackUrl.startsWith("/") && !callbackUrl.startsWith("//") && !callbackUrl.includes("://");

    return isInternalPath ? callbackUrl : null;
};

// Leaves the auth screens once the student is known. `authenticated` (below)
// also pushes the callbackUrl a moment later: going through /home first made
// the expired-subscription wizard flash before the callback page (e.g. the
// prompts library, which is allowed without an active plan) took over, so the
// callback wins here too.
sample({
    source: {
        $student,
        $activeRoute
    },
    filter: (s) => {
        return s.$student !== null && !!["auth/", "app-onboarding"].find((r) => (s.$activeRoute ?? "").includes(r));
    },
    fn: () => getInternalCallbackUrl() ?? "/home",
    target: routerPush
});

sample({
    clock: [authenticated, authenticateFx.finally, $activeRoute.updates],
    source: {
        $student,
        $activeRoute,
        $isApp,
        $platform,
        authPending: authenticateFx.pending,
        authChecked: $authChecked
    },
    filter: ({ $student, $activeRoute, authPending, authChecked }) => {
        if (!authChecked || authPending || $student !== null || $activeRoute === "/") return false;
        if (notRequireAuthPaths.some((p) => ($activeRoute ?? "").includes(p))) return false;
        if (typeof window !== "undefined" && new URLSearchParams(window.location.search).get("token")) {
            return false;
        }
        return true;
    },
    fn: ({ $isApp, $platform, $activeRoute }) => {
        if ($isApp) {
            const version = typeof window !== "undefined" ? localStorage.getItem("app_onboarding_version") : null;
            const path = version === "v2" ? "/app-onboarding/v2" : "/app-onboarding";
            return `${path}?platform=${$platform}`;
        }

        if ($activeRoute && $activeRoute !== "/home" && !$activeRoute.startsWith("/profile")) {
            const search = typeof window !== "undefined" ? window.location.search : "";
            const fullPath = $activeRoute + search;
            return `/auth/signin?callbackUrl=${encodeURIComponent(fullPath)}`;
        }

        return "/auth/signin";
    },
    target: routerPush
});

sample({
    clock: logoutStudent,
    source: { $activeRoute, $isApp, $platform },
    filter: ({ $activeRoute }) => {
        if (notRequireAuthPaths.some((p) => ($activeRoute ?? "").includes(p))) return false;
        if ($activeRoute === "/") return false;
        if (
            typeof window !== "undefined" &&
            ($activeRoute ?? "").startsWith("/upsell") &&
            new URLSearchParams(window.location.search).get("token")
        )
            return false;
        return true;
    },
    fn: ({ $isApp, $platform, $activeRoute }) => {
        if ($isApp) {
            const version = typeof window !== "undefined" ? localStorage.getItem("app_onboarding_version") : null;
            const path = version === "v2" ? "/app-onboarding/v2" : "/app-onboarding";
            return `${path}?platform=${$platform}`;
        }

        if ($activeRoute && $activeRoute !== "/home" && !$activeRoute.startsWith("/profile")) {
            const search = typeof window !== "undefined" ? window.location.search : "";
            const fullPath = $activeRoute + search;
            return `/auth/signin?callbackUrl=${encodeURIComponent(fullPath)}`;
        }

        return "/auth/signin";
    },
    target: routerPush
});

sample({
    clock: authenticated,
    source: {
        $student,
        $activeRoute
    },
    filter: ({ $student, $activeRoute }) => {
        const protectedPaths = ["/experiment", "/onboarding", "/auth", "/creator", "/luck", "/creator"];
        const currentRoute = $activeRoute ?? "";

        return $student !== null && protectedPaths.some((path) => currentRoute.startsWith(path));
    },
    fn: () => {
        if (typeof window !== "undefined") {
            const callbackUrl = getInternalCallbackUrl();

            if (callbackUrl) {
                if (callbackUrl.startsWith("/upsell")) {
                    const upsellPath = callbackUrl.toLowerCase();
                    if (upsellPath.includes("ai_tools")) {
                        localStorage.setItem("prevUrl", "/assistant");
                    } else if (
                        upsellPath.includes("30k_prompts") ||
                        upsellPath.includes("30k_prompts_sale") ||
                        upsellPath.includes("30k_prompts_complete_pack") ||
                        upsellPath.includes("30k_prompts_complete_pack_sale")
                    ) {
                        localStorage.setItem("prevUrl", "/prompts-library?pack=ultimate_ai");
                    } else if (upsellPath.includes("600_prompts")) {
                        localStorage.setItem("prevUrl", "/prompts-library?pack=personal_boost");
                    }
                }

                return callbackUrl;
            }
        }
        return "/home";
    },
    target: routerPush
});

sample({
    clock: authenticated,
    source: { $student },
    filter: (s) => {
        if (typeof window === "undefined") return false;

        const currentPath = window.location.pathname;

        return s.$student !== null && currentPath.includes("/app-onboarding");
    },
    fn: () => "/home",
    target: routerPush
});

sample({
    clock: restoreApplePurchaseFx.doneData,
    target: $appleRestoreResult
});

sample({
    clock: restoreApplePurchaseFx.doneData,
    filter: (token) => token !== null,
    fn: (token: string) => {
        HttpRequest.tokenStorage.setToken(token);
        return token;
    }
});

sample({
    clock: restoreApplePurchaseFx.doneData,
    target: authenticateFx
});

sample({
    clock: restoreApplePurchaseFx.failData,
    fn: (error) => error.message || "Something went wrong.",
    target: $appleRestoreError
});

sample({
    clock: getAppleRestorePurchases,
    target: restoreApplePurchaseFx
});

sample({
    clock: appMounted,
    filter: () => {
        if (typeof window === "undefined") return false;
        return new URLSearchParams(window.location.search).get("token") !== null;
    },
    fn: () => {
        if (typeof window === "undefined") return "";
        return new URLSearchParams(window.location.search).get("token")!.trim();
    },
    target: magicLinkTokenDetected
});

sample({
    clock: magicLinkTokenDetected,
    target: verifyMagicLinkFx
});

sample({
    clock: verifyMagicLinkFx.doneData,
    filter: (token) => token !== null,
    fn: () => {
        if (typeof window === "undefined") return "";
        const params = new URLSearchParams(window.location.search);
        params.delete("token");
        const newSearch = params.toString();
        return window.location.pathname + (newSearch ? `?${newSearch}` : "");
    },
    target: routerReplace
});

sample({
    clock: verifyMagicLinkFx.doneData,
    filter: (token) => token !== null,
    target: [authenticateFx, magicLinkAuthenticated]
});

sample({
    clock: verifyMagicLinkFx.fail,
    fn: () => {
        if (typeof window === "undefined") return "/auth/signin";

        const params = new URLSearchParams(window.location.search);
        const rawEmail = params.get("email") ?? "";
        const email = rawEmail.replace(/^\{/, "").replace(/\}$/, "");

        params.delete("token");
        const search = params.toString();
        const callbackPath = window.location.pathname + (search ? `?${search}` : "");

        let url = `/auth/signin?callbackUrl=${encodeURIComponent(callbackPath)}`;
        if (email) {
            url += `&email=${encodeURIComponent(email)}`;
        }
        return url;
    },
    target: routerPush
});

sample({
    clock: studentProfileRefreshed,
    target: refreshStudentProfileFx
});

sample({
    clock: refreshStudentProfileFx.doneData,
    target: $student
});

const contract = new ModelContract(
    {
        $student,
        $signinError,
        $appleRestoreResult,
        $appleRestoreError,
        $magicLinkPending: verifyMagicLinkFx.pending
    },
    {
        signInRequested,
        signUpRequested,
        authenticated,
        logoutStudent,
        signinError,
        deleteProfileRequested,
        setStudentActiveLocally,
        magicLinkTokenDetected,
        magicLinkAuthenticated,
        studentProfileRefreshed
    },
    {
        signInRequestedFx: signInByEmailFx,
        signUpRequestedFx: signUpByEmailFx,
        confirmEmailAfterPurchaseFx,
        setPasswordWithoutCodeFx,
        getOnboardingDataFx,
        setUserIdInAmplitudeFx,
        setAmplitudeDeviceIdFromProviderFx
    },
    {}
);

export type IUserAuthContract = typeof contract;

export class UserAuthModule extends ModuleDescription<IUserAuthContract> {
    getDefaultContract() {
        return contract;
    }
}

export const $authPending = authenticateFx.pending;

export { $student, authenticateFx, $authChecked };
